import { type ReactElement } from "react";
import {
  ArrowUpRight,
  Bell,
  BookOpen,
  ChevronRight,
  Link2,
  Send,
  ShieldCheck,
} from "lucide-react";
import {
  AsyncSection,
  asyncStatus,
  Button,
  EmptyState,
  SectionHeader,
  SettingsDetailsButton,
  SettingsMetaCard,
  SettingsPageLayout,
  Skeleton,
} from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { useAuthStore } from "../../auth/auth-store.js";
import { canAdministerWorkspace } from "../../auth/roles.js";
import { getDocumentationUrl } from "../../config/runtime.js";
import {
  useNotificationPreferencesQuery,
  useSetNotificationPreference,
} from "../../notifications/useNotificationPreferences.js";
import { ADMIN_SECTION_ROUTES } from "../../routes.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { useGuardedNavigate } from "../../settings/leave-guard.js";
import { NotificationPreferenceList } from "./NotificationPreferenceList.js";

export function NotificationsSettings(): ReactElement {
  const preferences = useNotificationPreferencesQuery();
  const write = useSetNotificationPreference();
  const claims = useAuthStore((state) => state.claims);
  const isAdmin = canAdministerWorkspace(claims);
  const documentationUrl = getDocumentationUrl();
  const navigate = useGuardedNavigate();
  const formatter = useDateTimeFormatter();
  const events = preferences.data?.events ?? [];
  const enabledInApp = events.filter((event) =>
    event.channels.some(
      (channel) => channel.channel === "in_app" && channel.enabled,
    ),
  ).length;
  const enabledEmail = events.filter((event) =>
    event.channels.some(
      (channel) => channel.channel === "email" && channel.enabled,
    ),
  ).length;

  const meta = (
    <div className="flex flex-col gap-3.5">
      <SettingsMetaCard
        icon={Send}
        title="Delivery"
        description="Each alert follows its own channel setting and saves as soon as you change it."
        rows={[
          { label: "In-app", value: `${enabledInApp} enabled` },
          { label: "Email", value: `${enabledEmail} enabled` },
          { label: "Time zone", value: formatter.timeZone },
        ]}
      />
      {isAdmin ? (
        <SettingsMetaCard
          icon={ShieldCheck}
          title="Workspace policy"
          description="Workspace rules can require an alert, switch off a channel, or make a delivery option unavailable."
          action={
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => navigate(ADMIN_SECTION_ROUTES["communication"]!)}
            >
              Open communication settings
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </Button>
          }
        />
      ) : null}
      {documentationUrl ? (
        /* The documentation link, the Account page's card: it left the header
           so the Details button has the header's action space (PB-5436). */
        <SettingsMetaCard
          icon={Link2}
          title="Quick links"
          links={[
            {
              label: "Documentation",
              icon: BookOpen,
              href: documentationUrl,
              external: true,
              accessory: <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />,
            },
          ]}
        />
      ) : null}
    </div>
  );

  return (
    <div className="pb-motion-content-enter">
      <SectionHeader
        variant="settings"
        leading={<Bell className="w-4 h-4 text-pb-cyan-text" />}
        title="Notifications"
        description={`Choose which alerts reach you in ${PRODUCT_NAME} and by email.`}
        actionsSlot={<SettingsDetailsButton meta={meta} />}
      />
      <SettingsPageLayout meta={meta} mainWidth="fluid">
        <AsyncSection
          status={asyncStatus({
            isLoading: preferences.isLoading,
            isError: preferences.isError,
            isEmpty: preferences.isSuccess && events.length === 0,
            hasData: events.length > 0,
          })}
          loadingLabel="your notification settings"
          errorTitle="Couldn't load your notification settings"
          errorDescription={preferences.error?.message}
          onRetry={() => void preferences.refetch()}
          empty={
            <EmptyState
              title="No notification settings yet"
              description="Your workspace has not published any configurable alerts."
            />
          }
          skeleton={<PreferenceSkeleton />}
        >
          <NotificationPreferenceList
            events={events}
            isSaving={write.isPending}
            error={write.error}
            onDismissError={write.reset}
            onChange={write.setPreference}
          />
        </AsyncSection>
      </SettingsPageLayout>
    </div>
  );
}

function PreferenceSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-3.5">
      <Skeleton className="h-9" />
      <Skeleton className="h-20" />
      <Skeleton className="h-20" />
      <Skeleton className="h-20" />
      <Skeleton className="h-20" />
    </div>
  );
}
